/* eslint-disable no-underscore-dangle */
import { useSetupAICheck } from './check/setup';
import { AsyncGogoHost, loadGogoLib, loadVue3Stuff } from './runtime-loader';
import { defineComponent, onMounted, onUnmounted, provide, ref, shallowRef, watchEffect } from '@vue/composition-api';
import { notifyVue3RouterChanged } from './vue3router';
import { useStore } from '@/store';
import { prepareGogoRuntimeEnv } from './useGogoRuntimeEnv';
import { cloneDeep } from 'lodash';
import dependencyLoader from '@utils/dependencyLoader';
import { GOGO_DEBUG_LOG } from './constants';
export default defineComponent({
    name: 'GogoRuntimeRouterWrapper',
    components: {
        AsyncGogoHost,
    },
    props: {
        projectId: {
            type: String,
            required: true,
        },
        env: {
            type: String,
            required: true,
        },
        branch: {
            type: String,
            required: true,
        },
    },
    setup(props) {
        const { markReady } = useSetupAICheck();
        const loadLib = loadGogoLib('runtime'); // 触发加载 lib
        console.log('GogoRuntimeRouterWrapper: props', { ...props });
        const gogoHostKey = ref('gogoHost');
        const gogoHost = shallowRef(null); // 本来用不到，但是Vue2套Vue3就是魔法
        const gogoRuntimeViewInstances = shallowRef([]);
        provide('gogoHostRef', gogoHost); // see ./GogoRuntimeRouterView.vue
        provide('gogoRuntimeViewInstances', gogoRuntimeViewInstances);
        function handlePointerDown(e) {
            gogoHost?.value?.handlePointerDown?.value?.(e);
        }
        function abortEventIfPicking(e) {
            gogoHost?.value?.abortEventIfPicking?.value?.(e);
        }
        function handleGogoHostReady(controller) {
            console.log('GogoRuntimeRouterWrapper: gogoHost ready', controller);
            markReady(loadLib);
            gogoHost.value = controller.context;
            debug?.setup(controller);
            loadVue3Stuff().then((stuff) => {
                const { watchEffect } = stuff.vue;
                watchEffect(() => {
                    const gogoPage = gogoHost.value?.currentMatchedRoute.value?.gogoPage;
                    const { title, description, metaKeywords } = gogoPage?.meta ?? {};
                    const gogoHijack = window.__gogoDebugHijack;
                    if (gogoHijack)
                        gogoHijack.currentRouteIsGogoPage = gogoPage?.file || '';
                    // 没有渲染的时候，不需要更新 title 和 description
                    if (!gogoRuntimeViewInstances.value.length)
                        return;
                    if (title) {
                        document.title = title;
                    }
                    if (description) {
                        let el = document.querySelector('meta[name="description"]');
                        if (!el) {
                            el = document.createElement('meta');
                            el.setAttribute('name', 'description');
                            document.head.appendChild(el);
                        }
                        el.setAttribute('content', description);
                    }
                    if (metaKeywords) {
                        let el = document.querySelector('meta[name="keywords"]');
                        if (!el) {
                            el = document.createElement('meta');
                            el.setAttribute('name', 'keywords');
                            document.head.appendChild(el);
                        }
                        el.setAttribute('content', metaKeywords);
                    }
                });
                watchEffect(() => {
                    const isPicking = !!controller.context.editorStatusForRuntime?.value?.isPicking;
                    const gogoHijack = window.__gogoDebugHijack;
                    if (gogoHijack)
                        gogoHijack.isPicking = isPicking;
                });
            });
        }
        // #region 调试能力
        const wujiAiPageUK = new URL(window.location.href).searchParams?.get('wujiAiPageUK');
        const allowPicking = !!wujiAiPageUK && window.location.search?.includes('iframe=1');
        const debugIsErrorReportable = (error) => {
            const messageLineCount = error.message.split('\n').length;
            // 错误来源是 wuji_runtime 的错误，不上报
            if (/\bwuji_runtime\.\w+\.js\b/.test(error.stack?.split('\n')[messageLineCount] || ''))
                return false;
            return true; // 其他错误都上报
        };
        const debug = wujiAiPageUK ? useDebugBridge({
            wujiAiPageUK,
            onInitialData(e) {
                initialUserData.value = e;
                syncBackendHash(e?.backend?.hash || '');
            },
        }) : null;
        function syncBackendHash(backendHash = '') {
            const gogoHijack = window.__gogoDebugHijack;
            if (!gogoHijack)
                return; // 这个函数仅限开发模式
            if (backendHash === gogoHijack.backendHash)
                return;
            console.log(`[wuji-gogo] backend code changed. hash = ${backendHash}`);
            gogoHijack.backendHash = backendHash;
        }
        function handleDebugUserCodeChanged({ /* patch,  */ content }) {
            // 包含 HMR 也会触发这个回调函数
            syncBackendHash(content?.backend?.hash || '');
        }
        // #endregion
        const initialUserData = shallowRef(null);
        onMounted(async () => {
            const store = useStore();
            const aiProjectInfo = await store.dispatch('runtime/getAiProjectInfo', { projectId: props.projectId, env: props.env, branchId: props.branch });
            if (debug)
                return;
            initialUserData.value = aiProjectInfo?.artifact;
            console.log('initialUserData', initialUserData.value);
        });
        const gogoStatus = shallowRef('initializing');
        function handleGogoStatusChanged(status) {
            gogoStatus.value = status;
            if (status === 'executed') {
                const pages = gogoHost.value?.exports.value?.pageDescriptors;
                if (Array.isArray(pages)) {
                    notifyVue3RouterChanged({ pages });
                }
            }
            // console.log('GogoRuntimeRouterWrapper: status changed', status, gogoHost.value?.exports);
        }
        const prepareEnv = async (userData) => {
            if (GOGO_DEBUG_LOG) {
                console.log('[gogo-runtime-router-wrapper] prepareEnv', userData);
            }
            const ans = await prepareGogoRuntimeEnv({
                collectionRefs: cloneDeep(userData.meta?.collectionRefs || []),
                externals: cloneDeep(userData.externals || []),
                projectId: props.projectId,
                pageId: 'ai',
            });
            return ({
                globals: ans.globals,
                customRequire: ans.customRequire,
            });
        };
        return {
            gogoHostKey,
            initialUserData,
            prepareEnv,
            handleGogoHostReady,
            handleGogoStatusChanged,
            handlePointerDown,
            handleDebugUserCodeChanged,
            abortEventIfPicking,
            allowPicking,
            debugIsErrorReportable,
        };
    },
});
function useDebugBridge({ wujiAiPageUK, onInitialData }) {
    const isDisconnected = ref(false);
    const debugBridge = shallowRef(null);
    const isInitConnecting = ref(true); // 用户可能不小心复制了带有 wujiAiPageUK 的链接分享出去了...
    watchEffect((onCleanup) => {
        if (!isInitConnecting.value)
            return;
        const timer = setTimeout(() => {
            // remove the query string
            const url = new URL(window.location.href);
            if (!url.searchParams?.get('wujiAiPageUK'))
                return;
            url.searchParams.delete('wujiAiPageUK');
            window.location.href = url.toString();
        }, 500);
        onCleanup(() => clearTimeout(timer));
    });
    let debugBridgeForEditor;
    const channel = new MessageChannel();
    const port = channel.port1;
    port.onmessage = (e) => {
        // connected!
        if (isInitConnecting.value)
            isInitConnecting.value = false;
        if (isDisconnected.value)
            return; // 已经关闭了
        if (connectionTimeoutTimer) {
            clearTimeout(connectionTimeoutTimer);
            connectionTimeoutTimer = null;
        }
        if (e.data.type === '__preview-data__') {
            onInitialData(e.data.data);
            return;
        }
        if (e.data.type === '__debug-bridge_call_from_editor__') {
            const { method, args } = e.data;
            const port = e.data.port;
            Promise.resolve()
                .then(() => debugBridgeForEditor?.[method](...args))
                .then(result => port.postMessage({ result }), error => port.postMessage({ error }));
            return;
        }
        if (e.data.type === '__preview-bad-request__') {
            closeDebugConnection();
            return;
        }
        if (e.data.type === '__preview-close__') {
            // 还是调试模式，但是断开链接
            debugBridge.value = null;
            return;
        }
        if (e.data.type === '__editor-update-dependency__') {
            const { bundle, dependencies, port } = e.data;
            dependencyLoader.updatePreviewDeps({
                bundle,
                dependencies,
            }).then(() => {
                port.postMessage({
                    type: '__editor-update-dependency-done__',
                    time: Date.now(),
                });
            })
                .catch(error => port.postMessage({
                type: '__editor-update-dependency-error__',
                error,
            }));
        }
    };
    port.start();
    let connectionTimeoutTimer = setTimeout(() => {
        closeDebugConnection();
    }, 3000);
    function closeDebugConnection() {
        if (isDisconnected.value)
            return;
        isDisconnected.value = true;
        debugBridge.value = null;
        if (connectionTimeoutTimer) {
            clearTimeout(connectionTimeoutTimer);
            connectionTimeoutTimer = null;
        }
        port.close();
    }
    debugBridge.value = new Proxy(Object.freeze({}), // Vue 2 真是太好了
    {
        get(_, action) {
            return function (...args) {
                return new Promise((resolve, reject) => {
                    const channel = new MessageChannel();
                    channel.port1.onmessage = (e) => {
                        const { result, error } = e.data;
                        if (error)
                            reject(error);
                        else
                            resolve(result);
                        channel.port1.close();
                    };
                    channel.port1.start();
                    port?.postMessage({ type: '__debug-bridge__', action, args, port: channel.port2 }, [channel.port2]);
                });
            };
        },
    });
    const sendClosed = () => port?.postMessage({ type: '__preview-close__' });
    window.addEventListener('beforeunload', sendClosed);
    onUnmounted(() => {
        sendClosed();
        window.removeEventListener('beforeunload', sendClosed);
    });
    console.log('connecting preview with wujiAiPageUK', wujiAiPageUK, 'pageId');
    (window.opener || window.parent || window.top)?.postMessage({
        type: '__preview-iframe-ready__',
        key: wujiAiPageUK,
        port: channel.port2,
    }, '*', [channel.port2]);
    function setup(controller) {
        debugBridgeForEditor = controller.getDebugBridgeForEditor();
        controller.setDebugBridge(debugBridge.value);
    }
    return { setup };
}
